import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Loader from "../common/components/Loader";
import actions from "./actions";
import { useLocation } from "react-router-dom";
import "./softCosts.css";
import Portal from "../common/components/Portal";
import ConfirmationModal from "../common/components/ConfirmationModal";
import _, { isEqual } from "lodash";
import { checkPermission, showAlert as showAlertUtil } from "../../config/utils";
import { permissions } from "../../config/permissions";
import useSoftCostDynamicField from "./useSoftCostDynamicField";
import { EllipsisTooltipText } from "../common/components/EllipsisTextWithTooltip";
import commonActions from "../common/actions";
import ReactTooltip from "react-tooltip";
import { COLOR_PALETTE } from "./constants";
import { BulkDefaultsModal } from "./components/DefaultSettingsModal";
import { ExportPopup } from "./components/ExportModal";
import { ERROR_COPY } from "../../config/errorMessages";

const SoftCosts = props => {
    const dispatch = useDispatch();
    const { id, section } = useParams();
    const { getSoftCosts, excelData } = useSelector(state => state.softCostReducer);
    const { isFormDirty: isDirty } = useSelector(state => state.commonReducer);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const projectId = queryParams.get("pid") || props.projectId;

    const [recommendations, setRecommendations] = useState([
        {
            year: null,
            initialCsp: null,
            escalation: null,
            inflation: null,
            c_inflation: null,
            contingency: null,

            actual_contingency_percentage: null,
            actual_escalation: null,
            actual_inflation_percentage: null,
            actual_compound_inflation: null,

            dynamic_fields: {},
            actual_dynamic_fields: {}
        }
    ]);
    const [defaultStaticSettings, setDefaultStaticSettings] = useState({ escalation: 0, contingency: 0 });
    const [defaultDynamicSettings, setDefaultDynamicSettings] = useState({});

    const [initialData, setInitialData] = useState({
        recommendations: [],
        initialInflation: null,
        defaultDynamicSettings: {},
        defaultStaticSettings: { escalation: 0, contingency: 0 }
    });
    const [initialInflation, setInitialInflation] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [showConfirmModal, setConfirmationModal] = useState(false);
    const [showSaveConfirmation, setSaveConfirmation] = useState(false);
    const [isExporting, setExporting] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [yearSummary, setYearSummary] = useState({});
    const [showDefaultsModal, setShowDefaultsModal] = useState(false);
    const [confirmDefaults, setConfirmDefaults] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [showExportModal, setExportModal] = useState(false);
    const [hiddenYears, setHiddenYears] = useState([]);
    const {
        dynamicSoftCosts,
        setDynamicSoftCosts,
        onDynamicFieldAdd,
        onDynamicFieldEdit,
        onDynamicFieldDelete,
        renderDynamicSoftCostModal,
        renderDeleteConfirmationModal,
        refreshToggle
    } = useSoftCostDynamicField({ props, id });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refreshToggle]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [recommendations]);

    useEffect(() => {
        dispatch(commonActions.setFormDirty(_.isEqual(recommendations, initialData.recommendations) ? false : true));
    }, [recommendations, initialData.recommendations]);

    const handleToggleYear = year => {
        if (hiddenYears.includes(year)) {
            setHiddenYears(hiddenYears.filter(y => y !== year));
        } else {
            setHiddenYears([...hiddenYears, year]);
        }
        setTimeout(() => ReactTooltip.rebuild(), 0);
    };

    const getParams = () => {
        let params = {};
        if (section === "regioninfo") {
            params.region_id = id;
            params.project_id = projectId;
        } else if (section === "siteinfo") {
            params.site_id = id;
            params.project_id = projectId;
        } else if (section === "buildinginfo") {
            params.building_id = id;
            params.project_id = projectId;
        } else if (section === "recommendationsinfo") {
            params.recommendation_id = id;
            params.project_id = projectId;
        } else {
            params.project_id = id;
        }
        return params;
    };
    const fetchData = async () => {
        let params = getParams();
        await dispatch(actions.getSoftCostsData(params));
        setLoading(false);
    };

    useEffect(() => {
        setSoftCostsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getSoftCosts]);

    useEffect(() => {
        if (!recommendations.length || !dynamicSoftCosts.length) return;
        const newYearSummary = recommendations.reduce((acc, item) => {
            acc[item.year] = calculateYearlySummary(item.year, recommendations);
            return acc;
        }, {});
        setYearSummary(newYearSummary);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, dynamicSoftCosts]);

    const pendingSaveRef = React.useRef(false);

    useEffect(() => {
        if (pendingSaveRef.current) {
            pendingSaveRef.current = false;
            saveData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, initialInflation, defaultStaticSettings, defaultDynamicSettings]);

    const setSoftCostsData = () => {
        const {
            soft_costs = [],
            year_totals = {},
            initial_inflation_percentage,
            dynamic_fields = [],
            default_static_settings = {},
            default_dynamic_settings = {}
        } = getSoftCosts || {};

        Object.keys(year_totals).forEach(year => {
            const idx = soft_costs.findIndex(obj => obj.year === year);
            if (idx !== -1) {
                soft_costs[idx].year_total = year_totals[year];
            } else {
                soft_costs.push({
                    year,
                    escalation: "",
                    inflation_percentage: "",
                    compound_inflation: 1,
                    contingency_percentage: "",
                    year_total: year_totals[year],
                    actual_contingency_percentage: "",
                    actual_escalation: "",
                    actual_inflation_percentage: "",
                    actual_compound_inflation: "",
                    dynamic_fields: {},
                    actual_dynamic_fields: {}
                });
            }
        });

        const recom = soft_costs
            .filter(item => item.year && Object.keys(year_totals).includes(item.year))
            .sort((a, b) => a.year - b.year)
            .map((item, idx) => {
                const { light, dark } = COLOR_PALETTE[idx % COLOR_PALETTE.length];
                return {
                    year: item.year,
                    initialCsp: item.year_total,
                    escalation: item?.escalation ?? "",
                    inflation: item?.inflation_percentage ?? "",
                    c_inflation: item?.compound_inflation ?? "",
                    contingency: item?.contingency_percentage ?? "",
                    actual_contingency_percentage: item?.actual_contingency_percentage ?? "",
                    actual_escalation: item?.actual_escalation ?? "",
                    actual_inflation_percentage: item?.actual_inflation_percentage ?? "",
                    actual_compound_inflation: item?.actual_compound_inflation ?? "",
                    dynamic_fields: Object.fromEntries(dynamic_fields.map(f => [f.key, item.dynamic_fields?.[f.key] ?? 0])),
                    actual_dynamic_fields: Object.fromEntries(dynamic_fields.map(f => [f.key, item.actual_dynamic_fields?.[f.key] ?? 0])),
                    light,
                    dark
                };
            });

        setDynamicSoftCosts(dynamic_fields);
        setRecommendations(recom);

        const defaultHidden = soft_costs
            .filter(item => item.year && Object.keys(year_totals).includes(item.year) && (!item.year_total || item.year_total === 0))
            .map(item => item.year);
        setHiddenYears(defaultHidden);

        setDefaultStaticSettings({
            escalation: default_static_settings?.escalation ?? 0,
            contingency: default_static_settings?.contingency ?? 0
        });

        const updatedDefaultDynamicSettings = dynamic_fields.reduce((acc, f) => {
            acc[f.key] = default_dynamic_settings?.[f.key] ?? 0;
            return acc;
        }, {});
        setDefaultDynamicSettings(updatedDefaultDynamicSettings);

        setInitialInflation(initial_inflation_percentage);
        setInitialData({
            recommendations: recom,
            initialInflation: initial_inflation_percentage,
            defaultDynamicSettings: updatedDefaultDynamicSettings,
            defaultStaticSettings: {
                escalation: default_static_settings?.escalation ?? 0,
                contingency: default_static_settings?.contingency ?? 0
            }
        });

        setLoading(false);
    };

    const handleChangeInflation = (year, inflation) => {
        if (!isNaN(inflation)) {
            let tempRecom = recommendations;
            const indexToChange = tempRecom.findIndex(elem => elem.year === year);
            let prevInflation = parseFloat(tempRecom[indexToChange - 1]?.c_inflation || 1);

            tempRecom = tempRecom.map((elem, idx) => {
                if (idx === indexToChange) {
                    const c_inflation = prevInflation + prevInflation * (inflation / 100) || 1;
                    prevInflation = c_inflation;
                    return { ...elem, c_inflation, inflation };
                } else if (idx > indexToChange) {
                    const c_inflation = prevInflation + prevInflation * (elem.inflation / 100) || 1;
                    prevInflation = c_inflation;

                    return { ...elem, c_inflation };
                } else {
                    return elem;
                }
            });
            setRecommendations(tempRecom);
        }
    };

    const handleChangeSoftCost = (year, rate, key) => {
        if (!isNaN(rate)) {
            const updatedRecom = recommendations.map(item => (item.year === year ? { ...item, [key]: rate } : item));
            setRecommendations(updatedRecom);
        }
    };
    const handleChangeDynamicSc = (year, rate, key) => {
        if (!isNaN(rate)) {
            const updatedRecom = recommendations.map(item =>
                item.year === year ? { ...item, dynamic_fields: { ...item.dynamic_fields, [key]: rate } } : item
            );
            setRecommendations(updatedRecom);
        }
    };
    const handleChangeActualDynamicSc = (year, rate, key) => {
        if (!isNaN(rate)) {
            const updatedRecom = recommendations.map(item =>
                item.year === year ? { ...item, actual_dynamic_fields: { ...item.actual_dynamic_fields, [key]: rate } } : item
            );
            setRecommendations(updatedRecom);
        }
    };

    const getPercentage = (amount = 0, percentage = 0) => {
        const factor = percentage / 100;
        const percAmount = amount * factor;
        return percAmount;
    };

    const setCompoundInflationForAll = percentage => {
        const percFactor = percentage / 100;
        let prevInflation = 1;
        const updated = recommendations.map((item, idx) => {
            const newInflation = idx === 0 ? 1 : prevInflation + percFactor * prevInflation;
            prevInflation = newInflation;
            return { ...item, inflation: idx === 0 ? 0 : percentage, c_inflation: newInflation };
        });
        return updated;
    };

    const getSoftCostTotal = (type, recom) => {
        const total = dynamicSoftCosts.reduce((sc, curr) => {
            const key = curr.key;
            const value = type === "settings" ? recom?.dynamic_fields?.[key] : recom?.actual_dynamic_fields?.[key] || 0;
            return getPercentage(recom.initialCsp, value) + sc;
        }, 0);
        return total;
    };

    const getTotalCosts = ({ key, type, dynamicFieldKey }) => {
        let sum = 0;
        if (!recommendations.length) return sum;
        let values = [];
        if (key === "initialCsp") {
            values = recommendations.map(item => item?.initialCsp);
        } else if (key === "dynamicField") {
            values = recommendations.map(item => getPercentage(item.initialCsp, item?.[type]?.[dynamicFieldKey] || 0));
        } else {
            values = Object.keys(yearSummary).map(year => yearSummary?.[year]?.[key]?.[type]?.amount || 0);
        }
        sum = values.reduce((acc, curr) => {
            return acc + curr;
        }, 0);
        return sum;
    };

    const saveData = async () => {
        setSubmitting(true);

        const { changedItems: changedSoftCosts, dynamic_field_present } = getChangedData(recommendations, initialData.recommendations);

        const hasInflationChanged = initialInflation !== initialData.initialInflation;
        const hasStaticChanged = !isEqual(defaultStaticSettings, initialData.defaultStaticSettings);
        const hasDynamicChanged = !isEqual(defaultDynamicSettings, initialData.defaultDynamicSettings);

        const shouldSave = changedSoftCosts.length > 0 || hasInflationChanged || hasStaticChanged || hasDynamicChanged;

        if (shouldSave) {
            let data = {
                ...(hasInflationChanged && {
                    initial_inflation_percentage: initialInflation || 0
                }),
                ...(changedSoftCosts.length > 0 && {
                    soft_costs: changedSoftCosts,
                    dynamic_field_present
                }),
                ...((hasStaticChanged || hasDynamicChanged) && {
                    default_dynamic_settings: defaultDynamicSettings,
                    default_static_settings: defaultStaticSettings
                }),
                ...getParams()
            };

            const result = dispatch(actions.saveSoftCostsData(data));
            if (result && typeof result.then === "function") {
                result
                    .then(() => {
                        setSubmitting(false);
                        fetchData();
                        showAlerts("Saved Successfully");
                    })
                    .catch(() => {
                        setSubmitting(false);
                        showAlerts(ERROR_COPY.GENERIC);
                    });
            } else {
                setSubmitting(false);
            }
        } else {
            setSubmitting(false);
            showAlerts("No changes detected");
        }
    };

    const isEmpty = value => {
        return value === null || value === undefined || value === "";
    };

    const getChangedData = (currentData, initialData) => {
        let dynamicFieldPresent = false;

        const changedItems = currentData.reduce((changedItems, currentItem) => {
            const initialItem = initialData.find(item => item.year === currentItem.year);
            let changedItem = { year: currentItem.year };

            Object.keys(currentItem).forEach(key => {
                if (key === "year") return;

                if (key !== "dynamic_fields" && key !== "actual_dynamic_fields") {
                    if (currentItem[key] !== initialItem[key]) {
                        const formattedKey =
                            key === "inflation"
                                ? "inflation_percentage"
                                : key === "contingency"
                                  ? "contingency_percentage"
                                  : key === "c_inflation"
                                    ? "compound_inflation"
                                    : key;

                        changedItem[formattedKey] = currentItem[key] ?? "";
                    }
                }

                if (key === "dynamic_fields" || key === "actual_dynamic_fields") {
                    const dynamicChanges = {};

                    Object.keys(currentItem[key] || {}).forEach(dynamicKey => {
                        if (currentItem[key][dynamicKey] !== (initialItem[key] || {})[dynamicKey]) {
                            dynamicChanges[dynamicKey] = currentItem[key][dynamicKey] ?? "";
                        }
                    });

                    if (Object.keys(dynamicChanges).length > 0) {
                        changedItem[key] = dynamicChanges;
                        dynamicFieldPresent = true;
                    }
                }
            });

            if (Object.keys(changedItem).length > 1) {
                changedItems.push(changedItem);
            }

            return changedItems;
        }, []);

        return {
            changedItems,
            dynamic_field_present: dynamicFieldPresent
        };
    };

    const disableInflationInputs = section !== "projectinfo";

    const showAlerts = msg => showAlertUtil(msg);

    const onCancel = () => {
        setRecommendations(initialData.recommendations);
        setInitialInflation(initialData.initialInflation);
    };

    const renderConfirmationModal = () => {
        if (!showConfirmModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to discard all changes?"}
                        message={"This action cannot be reverted, are you sure that you need to discard changes?"}
                        onNo={() => setConfirmationModal(false)}
                        onYes={() => {
                            setConfirmationModal(false);
                            onCancel();
                        }}
                    />
                }
                onCancel={() => setConfirmationModal(false)}
            />
        );
    };

    const renderSaveConfirmationModal = () => {
        if (!showSaveConfirmation) return null;
        const msg = getConfirmationMessage();
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={msg}
                        message={"Do you want to proceed?"}
                        onNo={() => setSaveConfirmation(false)}
                        onYes={() => {
                            setSaveConfirmation(false);
                            saveData();
                        }}
                    />
                }
                onCancel={() => setSaveConfirmation(false)}
            />
        );
    };

    const getConfirmationMessage = () => {
        switch (section) {
            case "projectinfo":
                return `You are about to save changes at the Project level. 
                        This will apply the new values to all regions, sites, and buildings under this project, 
                        overwriting any existing values at those levels.`;

            case "regioninfo":
                return `You are updating values at the Region level. 
                        The changes will apply to all sites and buildings under this region, 
                        and the Project level will receive a calculated value based on the updated regions.`;

            case "siteinfo":
                return `You are making changes at the Site level. 
                        The updated values will apply to all buildings within this site, 
                        and calculated values will update at both the Region and Project levels.`;

            case "buildinginfo":
                return `You are updating values at the Building level. 
                        This change will trigger recalculated values for the Site, Region, and Project levels.`;
            case "recommendationsinfo":
                return `You are updating values at the Recommendation level. 
                        This change will trigger a recalculation of values upstream for the Building, Site, Region, and Project levels.`;

            default:
                return "Unknown section. Please select a valid section to save changes.";
        }
    };

    const toggleExportModal = () => setExportModal(!showExportModal);

    const renderExportModal = () => {
        if (!showExportModal) return null;
        const { client } = props.basicDetails || {};
        const { project_id } = getParams();
        return (
            <Portal
                body={
                    <ExportPopup
                        projectId={project_id}
                        clientId={client.id}
                        onDownload={exportSoftCost}
                        isExporting={isExporting}
                        onCancel={toggleExportModal}
                    />
                }
                onCancel={toggleExportModal}
            />
        );
    };

    const exportSoftCost = async (payload, fileType) => {
        let params = getParams();
        try {
            setExporting(true);
            await dispatch(actions.exportExcel({ ...payload, ...params }, fileType));
        } catch (error) {
            console.log(error);
            showAlerts("Export Failed");
        } finally {
            toggleExportModal();
            setExporting(false);
        }
    };

    const calculatePercentage = (value = 0, base = 0) => {
        if (base === 0 || isNaN(value) || isNaN(base)) {
            return 0;
        }
        let percentage = (value / base) * 100;
        return parseFloat(percentage.toFixed(3));
    };

    const calculateYearlySummary = (year, updatedRecom) => {
        const recom = updatedRecom.find(item => item.year === year);
        const csp = recom.initialCsp;

        const softCostTotal = {
            settings: {
                percentage: calculatePercentage(getSoftCostTotal("settings", recom), csp),
                amount: getSoftCostTotal("settings", recom) || 0
            },
            actual: {
                percentage: calculatePercentage(getSoftCostTotal("actual", recom), csp),
                amount: getSoftCostTotal("actual", recom) || 0
            }
        };
        const softCostCspSum = {
            settings: {
                percentage: calculatePercentage(softCostTotal.settings.amount + csp, csp),
                amount: softCostTotal.settings.amount + csp
            },
            actual: {
                percentage: calculatePercentage(softCostTotal.actual.amount + csp, csp),
                amount: softCostTotal.actual.amount + csp
            }
        };
        const escalation = {
            settings: {
                amount: getPercentage(softCostCspSum.settings.amount, recom.escalation) || 0
            },
            actual: {
                amount: getPercentage(softCostCspSum.actual.amount, recom.actual_escalation) || 0
            }
        };
        const escalated = {
            settings: {
                percentage: calculatePercentage(escalation.settings.amount + softCostCspSum.settings.amount, csp),
                amount: escalation.settings.amount + softCostCspSum.settings.amount
            },
            actual: {
                percentage: calculatePercentage(escalation.actual.amount + softCostCspSum.actual.amount, csp),
                amount: escalation.actual.amount + softCostCspSum.actual.amount
            }
        };
        const contingency = {
            settings: {
                amount: getPercentage(escalated.settings.amount, recom.contingency) || 0
            },
            actual: {
                amount: getPercentage(escalated.actual.amount, recom.actual_contingency_percentage) || 0
            }
        };
        const contingencyTotal = {
            settings: {
                percentage: calculatePercentage(escalated.settings.amount + contingency.settings.amount || 0, csp),
                amount: escalated.settings.amount + contingency.settings.amount || 0
            },
            actual: {
                percentage: calculatePercentage(escalated.actual.amount + contingency.actual.amount || 0, csp),
                amount: escalated.actual.amount + contingency.actual.amount || 0
            }
        };
        const inflated = {
            settings: {
                percentage: calculatePercentage(contingencyTotal.settings.amount * (recom.c_inflation || 1), csp),
                amount: contingencyTotal.settings.amount * (recom.c_inflation || 1) || 0
            },
            actual: {
                percentage: calculatePercentage(contingencyTotal.actual.amount * (recom.c_inflation || 1), csp),
                amount: contingencyTotal.actual.amount * (recom.c_inflation || 1) || 0
            }
        };
        const inflation = {
            settings: {
                amount: inflated.settings.amount ? inflated.settings.amount - contingencyTotal.settings.amount : 0
            },
            actual: {
                amount: inflated.actual.amount ? inflated.actual.amount - contingencyTotal.actual.amount : 0
            }
        };

        return {
            softCostTotal,
            softCostCspSum,
            escalation,
            escalated,
            contingency,
            contingencyTotal,
            inflated,
            inflation
        };
    };

    const getSectionWithName = () => {
        const { name = "", code = "" } = props.basicDetails || {};
        let scSection = "";
        let sectionName = "";

        switch (section) {
            case "projectinfo":
                scSection = "Project";
                sectionName = name;
                break;
            case "regioninfo":
                scSection = "Region";
                sectionName = name;
                break;
            case "siteinfo":
                scSection = "Site";
                sectionName = name;
                break;
            case "buildinginfo":
                scSection = "Building";
                sectionName = name;
                break;
            case "recommendationsinfo":
                scSection = "Recommendation";
                sectionName = code;
                break;
            default:
                break;
        }

        return (
            <>
                {`FCA CSP Total Costs > `}
                {scSection}
                {sectionName && ` > `}
                {sectionName && <span className="border-active active-col">{sectionName}</span>}
            </>
        );
    };

    const handleRefresh = () => {
        setLoading(true);
        fetchData();
    };

    const renderResetAllConfirmationModal = () => {
        if (!confirmReset) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading="Do you want to reset all data?"
                        message={
                            <>
                                <p>You’re about to reset all soft cost data for this project.</p>
                                <p>
                                    This will permanently delete <strong>Settings and Actual</strong> data for every region, site, building, and
                                    recommendation.
                                </p>
                                <p>This action cannot be undone.</p>
                                <p>Proceed?</p>
                            </>
                        }
                        onNo={() => setConfirmReset(false)}
                        onYes={() => {
                            setConfirmReset(false);
                            handleResetAll();
                        }}
                    />
                }
                onCancel={() => setConfirmReset(false)}
            />
        );
    };

    const handleResetAll = () => {
        setLoading(true);
        const result = dispatch(actions.resetSoftCost(id));
        if (result && typeof result.then === "function") {
            result
                .then(() => {
                    showAlerts(`Soft cost reset completed successfully.`);
                    fetchData();
                })
                .catch(() => {
                    setLoading(false);
                    showAlerts(ERROR_COPY.GENERIC);
                });
        } else {
            setLoading(false);
        }
    };

    const openDefaults = () => setShowDefaultsModal(true);
    const closeDefaults = () => setShowDefaultsModal(false);

    const handleDefaultsSave = vals => {
        bulkValuesRef.current = vals;
        setConfirmDefaults(true);
    };
    const bulkValuesRef = React.useRef({});

    const onConfirmDefaults = () => {
        const payload = bulkValuesRef.current;
        const { initialInflation: perc, escalation: esc, contingency: cont, dynamic_fields: dyn } = payload;

        const hasPerc = "initialInflation" in payload;
        const hasEsc = "escalation" in payload;
        const hasCont = "contingency" in payload;
        const hasDyn = "dynamic_fields" in payload;

        if (settingsFieldDisabled) {
            let updated = recommendations.map(item => ({
                ...item,
                ...(hasEsc && { actual_escalation: esc }),
                ...(hasCont && { actual_contingency_percentage: cont }),
                ...(hasDyn && { actual_dynamic_fields: { ...(item.actual_dynamic_fields || {}), ...dyn } })
            }));
            if (hasEsc || hasCont) {
                setDefaultStaticSettings(prev => ({
                    ...prev,
                    ...(hasEsc && { escalation: esc }),
                    ...(hasCont && { contingency: cont })
                }));
            }
            if (hasDyn) {
                setDefaultDynamicSettings(prev => ({ ...prev, ...dyn }));
            }
            setRecommendations(updated);
        } else {
            let updated = hasPerc ? setCompoundInflationForAll(perc) : recommendations;
            updated = updated.map(item => ({
                ...item,
                ...(hasEsc && { escalation: esc }),
                ...(hasCont && { contingency: cont }),
                ...(hasDyn && { dynamic_fields: { ...(item.dynamic_fields || {}), ...dyn } })
            }));
            if (hasPerc) setInitialInflation(perc);
            if (hasEsc || hasCont) {
                setDefaultStaticSettings(prev => ({
                    ...prev,
                    ...(hasEsc && { escalation: esc }),
                    ...(hasCont && { contingency: cont })
                }));
            }
            if (hasDyn) {
                setDefaultDynamicSettings(prev => ({ ...prev, ...dyn }));
            }
            setRecommendations(updated);
        }

        closeDefaults();
        setConfirmDefaults(false);
        pendingSaveRef.current = true;
    };

    const hasEdit = checkPermission("forms", permissions.SOFT_COSTS, "edit");
    const hasCreate = checkPermission("forms", permissions.SOFT_COSTS, "create");
    const hasDelete = checkPermission("forms", permissions.SOFT_COSTS, "delete");
    const settingsFieldDisabled = section === "recommendationsinfo" || !hasEdit;
    const actualFieldDisabled = section !== "recommendationsinfo" || !hasEdit;
    const hasExport = checkPermission("forms", permissions.SOFT_COSTS, "export");
    const isSuperAdmin = localStorage.getItem("role") === "super_admin";
    return (
        <LoadingOverlay active={getSoftCosts?.loading || isLoading} spinner={<Loader />} fadeSpeed={10}>
            <div className="tab-active location-sec image-sec tab-grey efci-otr fund-efci pb-0">
                {renderConfirmationModal()}
                {renderSaveConfirmationModal()}
                {renderDynamicSoftCostModal()}
                {renderDeleteConfirmationModal()}
                {renderResetAllConfirmationModal()}
                {renderExportModal()}
                {showDefaultsModal && (
                    <Portal
                        body={
                            <BulkDefaultsModal
                                onCancel={closeDefaults}
                                onSave={handleDefaultsSave}
                                initialValues={{
                                    initialInflation,
                                    escalation: defaultStaticSettings.escalation,
                                    contingency: defaultStaticSettings.contingency,
                                    dynamic_fields: defaultDynamicSettings
                                }}
                                dynamicFields={dynamicSoftCosts}
                                disableInflationInputs={disableInflationInputs}
                                settingsFieldDisabled={settingsFieldDisabled}
                            />
                        }
                        onCancel={closeDefaults}
                    />
                )}

                {confirmDefaults && (
                    <Portal
                        body={
                            <ConfirmationModal
                                type="confirm"
                                heading={`Apply default ${settingsFieldDisabled ? "actual" : "settings"} to all years?`}
                                message={
                                    <>
                                        <p>This will update the modified {settingsFieldDisabled ? "actual" : "setting"} values for every year.</p>
                                        <p>{settingsFieldDisabled ? "Settings will remain unchanged." : "Actuals will remain unchanged."}</p>
                                        {!settingsFieldDisabled && section !== "buildinginfo" && (
                                            <p>
                                                This will also change default settings of{" "}
                                                {section === "projectinfo"
                                                    ? "region, site, and building"
                                                    : section === "regioninfo"
                                                      ? "site and building"
                                                      : section === "siteinfo"
                                                        ? "building"
                                                        : null}
                                                .
                                            </p>
                                        )}
                                        <p>Proceed?</p>
                                    </>
                                }
                                onNo={() => setConfirmDefaults(false)}
                                onYes={onConfirmDefaults}
                            />
                        }
                        onCancel={() => setConfirmDefaults(false)}
                    />
                )}

                <div className={`dtl-sec col-md-12 mt-0`}>
                    <div className="head-wrapper d-flex">
                        <div className="otr-topr d-flex">
                            {/* <h3>{getSectionWithName()}</h3> */}
                            {hasEdit && (
                                <div className="soft-btnotr">
                                    <button className="soft-btn" onClick={openDefaults}>
                                        <i class="fas fa-sliders-h"></i>Default {settingsFieldDisabled ? "Actual" : "Settings"}
                                    </button>
                                </div>
                            )}

                            <div className="soft-btnotr">
                                <button className="soft-btn" onClick={handleRefresh}>
                                    <i class="fas fa-sync"></i> Refresh
                                </button>
                            </div>
                            {section === "projectinfo" && isSuperAdmin && (
                                <div className="soft-btnotr reset-btn">
                                    <button className="soft-btn " onClick={() => setConfirmReset(true)}>
                                        <i class="fas fa-trash"></i>Reset All
                                    </button>
                                </div>
                            )}

                            {!disableInflationInputs && hasCreate && (
                                <div className="soft-btnotr">
                                    <button className="soft-btn" onClick={() => onDynamicFieldAdd()}>
                                        <i class="fas fa-plus"></i> Add Soft Cost
                                    </button>
                                </div>
                            )}
                            {hasExport && (
                                <div className="soft-btnotr">
                                    <button className="soft-btn" onClick={toggleExportModal}>
                                        <i class="fas fa-download"></i> Export
                                    </button>
                                </div>
                            )}
                            <div className="soft-btnotr project-cost-total-top">
                                {section === "recommendationsinfo" ? (
                                    <span className="label">Project Cost Actual Total Value:</span>
                                ) : (
                                    <span className="label" data-tip="Project Cost Actual Total Value" data-place="top" data-for="projectCost">
                                        <i className="fas fa-info-circle"></i>
                                    </span>
                                )}
                                <span className="value">
                                    <NumberFormat
                                        displayType="text"
                                        value={getTotalCosts({ key: "inflated", type: "actual" })}
                                        decimalScale={0}
                                        prefix={"$ "}
                                        thousandSeparator={true}
                                    />
                                </span>
                            </div>
                        </div>
                        <div className="filter-sec-plan d-flex pr-0">
                            {hasEdit && (
                                <div className="add-btn-wrapper pt-0">
                                    <button
                                        className={`button btn-discard ml-2 ${!isDirty ? "disbled-butn" : ""}`}
                                        disabled={!isDirty || isSubmitting}
                                        onClick={() => setConfirmationModal(true)}
                                    >
                                        Discard Changes
                                    </button>
                                    <button
                                        className={`button btn-save ml-2 cursor-hand ${!isDirty ? "disbled-butn" : ""}`}
                                        disabled={!isDirty || isSubmitting}
                                        onClick={() => setSaveConfirmation(true)}
                                    >
                                        Save
                                        {isSubmitting ? <span className="spinner-border spinner-border-sm pl-2 ml-1" role="status"></span> : null}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <ReactTooltip
                        id="projectCost"
                        className="rc-tooltip-custom-class"
                        backgroundColor="#007bff"
                        place="left"
                        effect="solid"
                        delayShow={500}
                    />
                    {/* add extra div 'custom-table-scroll' for scroll issue */}
                    <div className="custom-table-scroll">
                        <div className="table-section table-plan table-scroll build-fci fc-capital-nw build-efci proj-soft-cost">
                            <table className="table table-common-outer">
                                <tbody>
                                    <tr>
                                        <td>
                                            <table className="table table-common table-froze-soft">
                                                <thead>
                                                    <tr className="table-head-title table-row-sf">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add title-soft" typeof="input">
                                                            Title
                                                        </th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={item.year}
                                                                        className="collapsed-year-header"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    >
                                                                        <div
                                                                            className="collapsed-year-strip"
                                                                            style={{ backgroundColor: item.dark }}
                                                                            onClick={() => handleToggleYear(item.year)}
                                                                            data-tip="Expand Year"
                                                                            data-for="projectCost"
                                                                            data-place="top"
                                                                        >
                                                                            <i className="fas fa-plus expand-icon"></i>
                                                                            <span className="year-label-v">{item.year}</span>
                                                                        </div>
                                                                    </th>
                                                                );
                                                            }
                                                            return (
                                                                <th
                                                                    key={item.year}
                                                                    className="soft-cost-cell gap-x text-center year-header-cell"
                                                                    style={{ backgroundColor: item.dark }}
                                                                >
                                                                    {item.year}
                                                                    <button
                                                                        className="collapse-year-btn"
                                                                        onClick={() => handleToggleYear(item.year)}
                                                                        data-tip="Collapse Year"
                                                                        data-for="projectCost"
                                                                        data-place="top"
                                                                    >
                                                                        <i className="fas fa-minus"></i>
                                                                    </button>
                                                                </th>
                                                            );
                                                        })}
                                                        <th className="soft-cost-cell gap-x text-center">Total Costs</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="blue-highlight-row brd-gap">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add title-soft">Total Construction Costs</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <th key={item.year} className="build-add gap-x text-center dark-txt">
                                                                    <NumberFormat
                                                                        displayType="text"
                                                                        value={item.initialCsp}
                                                                        prefix={"$ "}
                                                                        thousandSeparator={true}
                                                                    />
                                                                </th>
                                                            );
                                                        })}
                                                        <th className="build-add blue-bg-col gap-x text-center dark-txt">
                                                            <NumberFormat
                                                                displayType="text"
                                                                value={getTotalCosts({ key: "initialCsp" })}
                                                                decimalScale={0}
                                                                prefix={"$ "}
                                                                thousandSeparator={true}
                                                            />
                                                        </th>
                                                    </tr>

                                                    <tr className="tb-row soft-cost-tr">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add soft-cost-title title-soft">Soft Costs</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <th key={`${item.year}-settings`} className="build-add value-pad gap-x">
                                                                    <div className="d-flex setting-actual-outer col-md-12">
                                                                        <div className="col-md-6 txt-justify-center">Setting</div>
                                                                        <div className="col-md-6 txt-justify-center">Actual</div>
                                                                    </div>
                                                                </th>
                                                            );
                                                        })}
                                                        <th key={`action`} className="build-add value-pad sticky-th-top gap-x">
                                                            <div className="d-flex setting-actual-outer col-md-12">
                                                                <div className="col-md-6 txt-justify-center">Setting</div>
                                                                <div className="col-md-6 txt-justify-center">Actual</div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    {dynamicSoftCosts.map((sc, idx) => (
                                                        <tr>
                                                            <th className="img-sq-box">
                                                                <img src="/img/sq-box.png" alt="" />
                                                            </th>
                                                            <th className="build-add wide-add">
                                                                <div className="outer-build-section d-flex">
                                                                    <EllipsisTooltipText className="sc-name" text={sc.name} />
                                                                    {idx !== 0 && !disableInflationInputs && (hasEdit || hasDelete) && (
                                                                        <span className="action-menu">
                                                                            <span className="outer-menu-action">
                                                                                {hasEdit && (
                                                                                    <i
                                                                                        class="fas fa-pencil-alt"
                                                                                        onClick={() => onDynamicFieldEdit(sc)}
                                                                                    >
                                                                                        {" "}
                                                                                    </i>
                                                                                )}
                                                                                {hasEdit && hasDelete && "  "}
                                                                                {hasDelete && (
                                                                                    <i
                                                                                        class="fas fa-trash-alt"
                                                                                        onClick={() => onDynamicFieldDelete(sc)}
                                                                                    >
                                                                                        {" "}
                                                                                    </i>
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </th>
                                                            {recommendations.map(item => {
                                                                if (hiddenYears.includes(item.year)) {
                                                                    return (
                                                                        <th
                                                                            key={`${item.year}-${sc.key}-collapsed`}
                                                                            className="collapsed-year-body"
                                                                            style={{ backgroundColor: item.dark }}
                                                                        ></th>
                                                                    );
                                                                }
                                                                return (
                                                                    <>
                                                                        <th
                                                                            key={`${item.year}-${sc.key}-settings-input`}
                                                                            className="build-add set-value gap-x"
                                                                        >
                                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                                <div className="d-flex col-md-6 align-items-center">
                                                                                    <div className="percentage-value">
                                                                                        <NumberFormat
                                                                                            disabled={settingsFieldDisabled}
                                                                                            className={`build-add ${
                                                                                                settingsFieldDisabled ? "cursor-disabled" : ""
                                                                                            }`}
                                                                                            displayType="input"
                                                                                            allowNegative={false}
                                                                                            value={item.dynamic_fields?.[sc.key] ?? ""}
                                                                                            onValueChange={value =>
                                                                                                handleChangeDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key
                                                                                                )
                                                                                            }
                                                                                            suffix=" %"
                                                                                            allowEmptyFormatting={true}
                                                                                            decimalScale={3}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="text-number">
                                                                                        <NumberFormat
                                                                                            className="build-add cursor-disabled"
                                                                                            thousandSeparator={true}
                                                                                            displayType="text"
                                                                                            value={getPercentage(
                                                                                                item.initialCsp,
                                                                                                item?.dynamic_fields?.[sc.key]
                                                                                            )}
                                                                                            prefix="$ "
                                                                                            decimalScale={0}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                <div className="d-flex col-md-6 align-items-center">
                                                                                    <div className="percentage-value">
                                                                                        <NumberFormat
                                                                                            disabled={actualFieldDisabled}
                                                                                            className={`build-add ${
                                                                                                actualFieldDisabled ? "cursor-disabled" : ""
                                                                                            }`}
                                                                                            displayType="input"
                                                                                            allowNegative={false}
                                                                                            value={item.actual_dynamic_fields?.[sc.key] ?? ""}
                                                                                            onValueChange={value =>
                                                                                                handleChangeActualDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key
                                                                                                )
                                                                                            }
                                                                                            suffix=" %"
                                                                                            allowEmptyFormatting={true}
                                                                                            decimalScale={3}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="text-number">
                                                                                        <NumberFormat
                                                                                            className="build-add cursor-disabled"
                                                                                            thousandSeparator={true}
                                                                                            displayType="text"
                                                                                            value={getPercentage(
                                                                                                item.initialCsp,
                                                                                                item?.actual_dynamic_fields?.[sc.key]
                                                                                            )}
                                                                                            prefix="$ "
                                                                                            decimalScale={0}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </th>
                                                                    </>
                                                                );
                                                            })}
                                                            <th className="build-add set-value gap-x">
                                                                <div className="d-flex col-md-12 per-value-outer">
                                                                    <div className="d-flex col-md-6 align-items-center">
                                                                        <div className="text-number">
                                                                            <NumberFormat
                                                                                displayType="text"
                                                                                value={getTotalCosts({
                                                                                    key: "dynamicField",
                                                                                    type: "dynamic_fields",
                                                                                    dynamicFieldKey: sc.key
                                                                                })}
                                                                                decimalScale={0}
                                                                                prefix={"$ "}
                                                                                thousandSeparator={true}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex col-md-6 align-items-center">
                                                                        <div className="text-number">
                                                                            <NumberFormat
                                                                                displayType="text"
                                                                                value={getTotalCosts({
                                                                                    key: "dynamicField",
                                                                                    type: "actual_dynamic_fields",
                                                                                    dynamicFieldKey: sc.key
                                                                                })}
                                                                                decimalScale={0}
                                                                                prefix={"$ "}
                                                                                thousandSeparator={true}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </th>
                                                        </tr>
                                                    ))}
                                                    <tr className="blue-bg-row">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Soft Cost Total</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-soft-cost-total-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                        style={{ backgroundColor: item.light }}
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostTotal?.settings
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostTotal?.settings?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostTotal?.actual
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostTotal?.actual?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value blue-bg-col gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostTotal", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostTotal", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    <tr className="blue-bg-row brd-gap">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Sub Total</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-soft-cost-sub-total-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                        style={{ backgroundColor: item.light }}
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostCspSum?.settings
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostCspSum?.settings?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostCspSum?.actual
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.softCostCspSum?.actual?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value blue-bg-col gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostCspSum", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostCspSum", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr>
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Escalation</th>

                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-escalation-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        disabled={settingsFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            settingsFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.escalation}
                                                                                        onValueChange={value =>
                                                                                            handleChangeSoftCost(
                                                                                                item.year,
                                                                                                value.floatValue ?? "",
                                                                                                "escalation"
                                                                                            )
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.escalation?.settings?.amount}
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        disabled={actualFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            actualFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.actual_escalation}
                                                                                        onValueChange={value =>
                                                                                            handleChangeSoftCost(
                                                                                                item.year,
                                                                                                value.floatValue ?? "",
                                                                                                "actual_escalation"
                                                                                            )
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.escalation?.actual?.amount}
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "escalation", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "escalation", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    <tr className="blue-bg-row brd-gap">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Escalated</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-escalated-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                        style={{ backgroundColor: item.light }}
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.escalated?.settings?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.escalated?.settings?.amount}
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.escalated?.actual?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.escalated?.actual?.amount}
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value blue-bg-col gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "escalated", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "escalated", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr>
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Project Contingency</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-contingency-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        disabled={settingsFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            settingsFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.contingency}
                                                                                        onValueChange={value =>
                                                                                            handleChangeSoftCost(
                                                                                                item.year,
                                                                                                value.floatValue ?? "",
                                                                                                "contingency"
                                                                                            )
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.contingency?.settings?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        disabled={actualFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            actualFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.actual_contingency_percentage}
                                                                                        onValueChange={value =>
                                                                                            handleChangeSoftCost(
                                                                                                item.year,
                                                                                                value.floatValue ?? "",
                                                                                                "actual_contingency_percentage"
                                                                                            )
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.contingency?.actual?.amount}
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "contingency", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "contingency", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    <tr className="blue-bg-row brd-gap">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add wide-add">
                                                            <div className="outer-build-section d-flex">
                                                                <EllipsisTooltipText className="sc-name" text={`Sub Total (Project Contingency)`} />
                                                            </div>
                                                        </th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-inflated-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                        style={{ backgroundColor: item.light }}
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.contingencyTotal?.settings
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.contingencyTotal?.settings
                                                                                                ?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.contingencyTotal?.actual
                                                                                                ?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        thousandSeparator={true}
                                                                                        displayType="text"
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.contingencyTotal?.actual?.amount
                                                                                        }
                                                                                        prefix="$ "
                                                                                        decimalScale={0}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value blue-bg-col gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "contingencyTotal", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "contingencyTotal", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr>
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Inflation</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-inflation-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className={`build-add ${
                                                                                            disableInflationInputs ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.inflation}
                                                                                        suffix={` %`}
                                                                                        allowEmptyFormatting={true}
                                                                                        disabled={disableInflationInputs}
                                                                                        onValueChange={(values, sourceInfo) => {
                                                                                            if (sourceInfo?.source !== "prop") {
                                                                                                handleChangeInflation(
                                                                                                    item.year,
                                                                                                    values.floatValue ?? ""
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        decimalScale={2}
                                                                                    />

                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        disabled={true}
                                                                                        displayType="input"
                                                                                        value={item.c_inflation}
                                                                                        decimalScale={3}
                                                                                        prefix="("
                                                                                        suffix=")"
                                                                                        data-tip={item.c_inflation}
                                                                                        data-for="projectCost"
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        displayType="text"
                                                                                        className="build-add cursor-disabled"
                                                                                        value={yearSummary?.[item.year]?.inflation?.settings?.amount}
                                                                                        thousandSeparator={true}
                                                                                        decimalScale={0}
                                                                                        prefix="$ "
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className={`build-add cursor-disabled`}
                                                                                        displayType="input"
                                                                                        allowNegative={false}
                                                                                        value={item.inflation}
                                                                                        suffix={` %`}
                                                                                        allowEmptyFormatting={true}
                                                                                        disabled={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        disabled={true}
                                                                                        displayType="input"
                                                                                        value={item.c_inflation}
                                                                                        decimalScale={3}
                                                                                        prefix="("
                                                                                        suffix=")"
                                                                                        data-tip={item.c_inflation}
                                                                                        data-for="projectCost"
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        displayType="text"
                                                                                        className="build-add cursor-disabled"
                                                                                        value={yearSummary?.[item.year]?.inflation?.actual?.amount}
                                                                                        thousandSeparator={true}
                                                                                        decimalScale={0}
                                                                                        prefix="$ "
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add set-value gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "inflation", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "inflation", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr className="blue-tot-bg-row">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Total Project Costs</th>
                                                        {recommendations.map(item => {
                                                            if (hiddenYears.includes(item.year)) {
                                                                return (
                                                                    <th
                                                                        key={`${item.year}-collapsed`}
                                                                        className="collapsed-year-body"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    ></th>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-grant-total-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                        style={{ backgroundColor: item.dark }}
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            yearSummary?.[item.year]?.inflated?.settings?.percentage
                                                                                        }
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        displayType="text"
                                                                                        className="build-add cursor-disabled"
                                                                                        value={yearSummary?.[item.year]?.inflated?.settings?.amount}
                                                                                        thousandSeparator={true}
                                                                                        decimalScale={0}
                                                                                        prefix="$ "
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        allowNegative={false}
                                                                                        value={yearSummary?.[item.year]?.inflated?.actual?.percentage}
                                                                                        suffix=" %"
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={3}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        className="build-add cursor-disabled"
                                                                                        displayType="text"
                                                                                        value={yearSummary?.[item.year]?.inflated?.actual?.amount}
                                                                                        thousandSeparator={true}
                                                                                        decimalScale={0}
                                                                                        prefix="$ "
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            );
                                                        })}
                                                        <th className="build-add gap-x sticky-th-bottom">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "inflated", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "inflated", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </LoadingOverlay>
    );
};
export default SoftCosts;
